/**
 * FactoryItemListView — card "Item List" trong Factory Data hub (/<code>/data).
 * Hiển thị bảng `public.list_and_valo` GIỐNG trang References ListAndValo của
 * Purchase Contract (yêu cầu chị Đẹp): tái dùng `PurchaseReferenceGrid` +
 * config `ListAndValo` + endpoint `/purchase-contracts/references/ListAndValo`
 * (list_and_valo là master data dùng chung, không scope theo factory).
 *
 * Read-only (như references). Header strip Back "Datasets" + accent pill khớp
 * bố cục các embed khác trong hub (SContractTab/deduction-rate).
 */
import { useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import { Box, Button, Stack, Typography, alpha } from '@mui/material'

import { filterBarBtnSx } from '@/components/dataList'
import { useGetPurchaseReferenceRowsQuery } from '@/store/api/purchaseContractApi'

import PurchaseReferenceGrid from '../../purchase/PurchaseReferenceGrid'
import {
  PURCHASE_REFERENCE_PAGE_CONFIGS,
  purchaseReferenceSheetColumnsMode,
} from '../../purchase/purchaseReferenceConfig'

// Accent = màu card 'item-list' trong FACTORY_DATA_HUB_META.
const ACCENT = '#145a59'

export default function FactoryItemListView({ onBackToHub }: { onBackToHub?: () => void }) {
  const [search, setSearch] = useState('')
  const page = PURCHASE_REFERENCE_PAGE_CONFIGS.ListAndValo
  const columnsMode = purchaseReferenceSheetColumnsMode('ListAndValo')

  const { data, isLoading, error, refetch } = useGetPurchaseReferenceRowsQuery(
    { pageKey: 'ListAndValo', limit: 1000000 },
    { refetchOnMountOrArgChange: true },
  )

  // Back "Datasets" + accent pill — nhúng TRONG FilterBar (1 thanh tích hợp,
  // bố cục giống tab /baoloc/consumables), không phải thanh header riêng.
  const headerSlot = onBackToHub ? (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBackToHub}
        sx={filterBarBtnSx}
      >
        Datasets
      </Button>
      <Stack
        direction="row"
        spacing={0.625}
        alignItems="center"
        sx={{
          mx: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: ACCENT,
          color: '#fff',
          boxShadow: `0 1px 3px ${alpha(ACCENT, 0.45)}`,
        }}
      >
        <Inventory2Icon sx={{ fontSize: '0.95rem', color: '#fff' }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            fontSize: '0.85rem',
            color: '#fff',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            whiteSpace: 'nowrap',
            lineHeight: 1.1,
          }}
        >
          Item List
        </Typography>
      </Stack>
    </>
  ) : undefined

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <PurchaseReferenceGrid
        page={page}
        columnsMode={columnsMode}
        rows={data?.items ?? []}
        search={search}
        onSearchChange={setSearch}
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        fillHeight
        headerSlot={headerSlot}
      />
    </Box>
  )
}
